import React, { useState, useEffect } from "react";
import "./NewScheduleForm.css";
import Layout from "../UI/Layout";
import { collection, getDocs, addDoc, doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import TimePicker24 from "../UI/TimePicker24";

const BLOCKS_FOR_SOLO = { 1: 7, 2: 7, 5: 1 };
const SOLO_DAYS_OFFSET = { 1: 0, 2: 1, 5: 4 };

const DAY_LABELS = {
  1: ["Route 1", "Route 2", "Route 3", "Route 4", "Route 5", "Route 6", "Route 7"],
  2: [
    // Week 1 — indices 0-3
    "Route 1", "Route 2", "Route 3", "Route 4",
    // Week 2 — indices 4-6
    "Route 5", "Route 6", "Route 7",
  ],
  5: ["Route 1"],
};

// SOLO 2: first 4 blocks belong to week 1, last 3 belong to week 2
const SOLO2_WEEK1_COUNT = 4;
const SOLO2_WEEK2_COUNT = 3;

const emptyBlock = () => ({ startDate: "", endDate: "", startTime: "", endTime: "" });
const defaultForm = () => ({
  driverName: "",
  email: "",
  phone: "",
  fuelCard: "",
  truckId: "",
  solo: "",
  blocks: [],
  notes: "",
});

// ── Helpers ────────────────────────────────────────────
const addDays = (dateStr, days) => {
  if (!dateStr) return "";
  const [y, m, day] = dateStr.split("-").map(Number);
  const d = new Date(y, m - 1, day + days); // local date math, no UTC shift
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

/**
 * Formats a local Date as YYYY-MM-DD without any UTC shift.
 */
const toDateStr = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

/**
 * Returns the Sunday of the CURRENT week as a local Date.
 */
const getThisWeekSunday = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay());
};

/**
 * Returns the Sunday of NEXT week as a local Date.
 * Form submissions always target next week.
 */
const getNextWeekSunday = () => {
  const sun = getThisWeekSunday();
  return new Date(sun.getFullYear(), sun.getMonth(), sun.getDate() + 7);
};

const formatDisplayDate = (date) =>
  date.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });

// ── Snackbar ───────────────────────────────────────────
const Snackbar = ({ message, type, visible }) => (
  <div className={`sf-snackbar sf-snackbar-${type} ${visible ? "sf-snackbar-show" : ""}`}>
    <i className={`fas ${type === "success" ? "fa-check-circle" : "fa-exclamation-circle"}`}></i>
    {message}
  </div>
);

// ── Component ──────────────────────────────────────────
const NewScheduleForm = () => {
  const [employees, setEmployees] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [loadingEmps, setLoadingEmps] = useState(true);
  const [loadingTrucks, setLoadingTrucks] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ message: "", type: "success", visible: false });
  const [formData, setFormData] = useState(defaultForm());
  const [today, setToday] = useState("");

  const showSnackbar = (message, type = "success") => {
    setSnackbar({ message, type, visible: true });
    setTimeout(() => setSnackbar((s) => ({ ...s, visible: false })), 3500);
  };

  // ── Current date ───────────────────────────────────────
  useEffect(() => {
    const now = new Date();
    setToday(now.toISOString().split("T")[0]);
  }, []);

  // ── Load employees ─────────────────────────────────────
  useEffect(() => {
    const fetchEmps = async () => {
      try {
        const snap = await getDocs(collection(db, "employees"));
        const data = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setEmployees(data);
      } catch (e) {
        console.error(e);
        showSnackbar("Failed to load employees.", "error");
      }
      setLoadingEmps(false);
    };
    fetchEmps();
  }, []);

  // ── Load trucks — only INACTIVE ones ──────────────────
  useEffect(() => {
    const fetchTrucks = async () => {
      try {
        const snap = await getDocs(collection(db, "trucks"));
        const data = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          // Only show trucks that are NOT active (i.e. available to assign)
          .filter((t) => !t.status || t.status.toLowerCase() == "inactive")
          .sort((a, b) => (a.internalId || "").localeCompare(b.internalId || ""));
        setTrucks(data);
      } catch (e) {
        console.error(e);
        showSnackbar("Failed to load trucks.", "error");
      }
      setLoadingTrucks(false);
    };
    fetchTrucks();
  }, []);

  // ── Driver select → auto-fill contact info ─────────────
  const handleDriverSelect = (e) => {
    const name = e.target.value;
    const emp = employees.find((em) => em.name === name);
    setFormData((f) => ({
      ...f,
      driverName: name,
      email: emp?.email || "",
      phone: emp?.phone || "",
      fuelCard: emp?.fuelCard || "",
    }));
  };

  // ── Solo change → rebuild blocks ──────────────────────
  const handleSoloChange = (e) => {
    const val = e.target.value;
    const count = BLOCKS_FOR_SOLO[val] || 0;
    setFormData((f) => ({
      ...f,
      solo: val,
      blocks: Array.from({ length: count }, emptyBlock),
    }));
  };

  // ── Block field change + auto end-date ────────────────
  const handleBlockChange = (idx, field, value) => {
    setFormData((f) => {
      const blocks = [...f.blocks];
      const offset = SOLO_DAYS_OFFSET[f.solo] ?? 0;
      blocks[idx] = { ...blocks[idx], [field]: value };

      // When start date is picked, auto-compute end date
      if (field === "startDate" && value) {
        blocks[idx].endDate = addDays(value, offset);
      }
      return { ...f, blocks };
    });
  };

  // ── Reset ──────────────────────────────────────────────
  const resetForm = () => setFormData(defaultForm());

  // ── Submit → save to Firebase "schedules" collection ──
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.driverName) { showSnackbar("Please select a driver.", "error"); return; }
    if (!formData.truckId) { showSnackbar("Please select a truck.", "error"); return; }
    if (!formData.solo) { showSnackbar("Please select a SOLO type.", "error"); return; }

    setSaving(true);
    try {
      const sunday = getNextWeekSunday();
      const selectedTruck = trucks.find((t) => t.id === formData.truckId);

      const basePayload = {
        driverName: formData.driverName,
        email: formData.email,
        phone: formData.phone,
        fuelCard: formData.fuelCard,
        truckId: formData.truckId,
        truckLabel: selectedTruck?.internalId || "",
        solo: formData.solo,
        notes: formData.notes,
        createdAt: new Date(),
      };

      if (formData.solo === "2") {
        // ── SOLO 2: split into 2 documents ──────────────────────────
        const week1Sunday = sunday;
        const week1Saturday = new Date(week1Sunday); week1Saturday.setDate(week1Sunday.getDate() + 6);
        const week2Sunday = new Date(week1Sunday); week2Sunday.setDate(week1Sunday.getDate() + 7);
        const week2Saturday = new Date(week2Sunday); week2Saturday.setDate(week2Sunday.getDate() + 6);

        const week1Blocks = formData.blocks.slice(0, SOLO2_WEEK1_COUNT);
        const week2Blocks = formData.blocks.slice(SOLO2_WEEK1_COUNT);

        await addDoc(collection(db, "schedules"), {
          ...basePayload,
          blocks: week1Blocks,
          weekStart: toDateStr(week1Sunday),
          weekEnd: toDateStr(week1Saturday),
        });

        await addDoc(collection(db, "schedules"), {
          ...basePayload,
          blocks: week2Blocks,
          weekStart: toDateStr(week2Sunday),
          weekEnd: toDateStr(week2Saturday),
        });

      } else {
        // ── SOLO 1 / 5: single document ─────────────────────────────
        const saturday = new Date(sunday); saturday.setDate(sunday.getDate() + 6);
        await addDoc(collection(db, "schedules"), {
          ...basePayload,
          blocks: formData.blocks,
          weekStart: toDateStr(sunday),
          weekEnd: toDateStr(saturday),
        });
      }

      // Mark truck as active
      if (formData.truckId) {
        await updateDoc(doc(db, "trucks", formData.truckId), { status: "Active" });
        setTrucks((prev) => prev.filter((t) => t.id !== formData.truckId));
      }

      showSnackbar(
        formData.solo === "2"
          ? "SOLO 2 schedule saved for 2 weeks!"
          : "Schedule saved successfully!"
      );
      resetForm();
    } catch (err) {
      console.error(err);
      showSnackbar("Failed to save schedule.", "error");
    }
    setSaving(false);
  };

  const blockLabels = DAY_LABELS[formData.solo] || [];

  // Week range label — shows next week normally, or 2-week range for SOLO 2
  const weekLabel = (() => {
    const sun = getNextWeekSunday();
    const fmt = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    if (formData.solo === "2") {
      const end = new Date(sun); end.setDate(sun.getDate() + 13); // +13 = 2 weeks - 1 day
      return `${fmt(sun)} – ${fmt(end)} (2 Weeks)`;
    }
    const sat = new Date(sun); sat.setDate(sun.getDate() + 6);
    return `${fmt(sun)} – ${fmt(sat)}`;
  })();

  // Solo end-date hint text
  const soloHints = {
    "1": "Same-day route — end date = start date",
    "2": "2-day route — end date = start date + 1 day",
    "5": "5-day route — end date = start date + 4 days",
  };

  return (
    <Layout activePage="new-schedule">
      <div className="sf-page">
        <Snackbar {...snackbar} />

        {/* ── Header ── */}
        <div className="sf-page-header">
          <div>
            <h1 className="sf-page-title">Create New Schedule</h1>
            <p className="sf-page-sub">Schedule for Week of {weekLabel}</p>
          </div>
          <button type="button" className="sf-reset-btn" onClick={resetForm}>
            <i className="fas fa-redo"></i> Reset Form
          </button>
        </div>

        <form className="sf-form" onSubmit={handleSubmit}>

          {/* ── Driver Information ── */}
          <div className="sf-section">
            <h3 className="sf-section-title">
              <i className="fas fa-user"></i> Driver Information
            </h3>
            <div className="sf-grid sf-grid-2">

              {/* Driver Name */}
              <div className="sf-group">
                <label>Driver Name <span className="sf-req">*</span></label>
                {loadingEmps ? (
                  <div className="sf-loading-field">
                    <i className="fas fa-spinner fa-spin"></i> Loading...
                  </div>
                ) : (
                  <select value={formData.driverName} onChange={handleDriverSelect} required>
                    <option value="">Select a driver...</option>
                    {employees.map((emp) => (
                      <option key={emp.id} value={emp.name}>{emp.name}</option>
                    ))}
                  </select>
                )}
              </div>

              {/* Truck — only inactive trucks shown */}
              <div className="sf-group">
                <label>Truck <span className="sf-req">*</span></label>
                {loadingTrucks ? (
                  <div className="sf-loading-field">
                    <i className="fas fa-spinner fa-spin"></i> Loading...
                  </div>
                ) : trucks.length === 0 ? (
                  <div className="sf-loading-field">
                    No available trucks
                  </div>
                ) : (
                  <select
                    value={formData.truckId}
                    onChange={(e) => setFormData((f) => ({ ...f, truckId: e.target.value }))}
                    required
                  >
                    <option value="">Select a truck...</option>
                    {trucks.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.internalId}{t.amazonId ? ` (${t.amazonId})` : ""}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Read-only auto-filled fields */}
              <div className="sf-group">
                <label><i className="fas fa-envelope"></i> Email</label>
                <input type="text" value={formData.email} readOnly
                  placeholder="Auto-filled on driver selection" className="sf-readonly" />
              </div>
              <div className="sf-group">
                <label><i className="fas fa-phone"></i> Phone</label>
                <input type="text" value={formData.phone} readOnly
                  placeholder="Auto-filled on driver selection" className="sf-readonly" />
              </div>
              <div className="sf-group">
                <label><i className="fas fa-gas-pump"></i> Fuel Card</label>
                <input type="text" value={formData.fuelCard} readOnly
                  placeholder="Auto-filled on driver selection" className="sf-readonly" />
              </div>
              {/* Route Type Dropdown */}
              <div className="sf-group">
                <label>Route Type <span className="sf-req">*</span></label>
                <select value={formData.solo} onChange={handleSoloChange} required>
                  <option value="">Select route type...</option>
                  <option value="1">SOLO 1 - (7 routes)</option>
                  <option value="2">SOLO 2 - (4 routes)</option>
                  <option value="5">SOLO 5 - (1 route )</option>
                </select>
              </div>
            </div>
          </div>

          {/* ── Schedule Blocks ── */}
          {formData.solo && (
            <div className="sf-section">
              <h3 className="sf-section-title">
                <i className="fas fa-calendar-alt"></i> Schedule Blocks
                {/* Current date — right side of heading */}
                <span className="sf-section-date">
                  {today && formatDisplayDate(new Date(today + "T00:00:00"))}
                </span>
              </h3>
              {/* Sub-hint below the heading */}
              <p className="sf-blocks-hint">
                <i className="fas fa-info-circle"></i>
                All blocks are optional to fill · End date auto-fills when start date is set ·{" "}
                {/* {soloHints[formData.solo]} */}
              </p>

              <div className="sf-blocks">
                {formData.blocks.map((block, idx) => {
                  const isSolo2 = formData.solo === "2";

                  // Insert the week divider BEFORE index 4 for SOLO 2
                  const showWeek2Divider = isSolo2 && idx === SOLO2_WEEK1_COUNT;

                  // Compute which week's date range to show in the divider header
                  const week1Sun = getNextWeekSunday();
                  const week1Sat = new Date(week1Sun); week1Sat.setDate(week1Sun.getDate() + 6);
                  const week2Sun = new Date(week1Sun); week2Sun.setDate(week1Sun.getDate() + 7);
                  const week2Sat = new Date(week2Sun); week2Sat.setDate(week2Sun.getDate() + 6);
                  const fmt = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

                  return (
                    <React.Fragment key={idx}>
                      {/* ── Week 1 header (shown before first block of SOLO 2) ── */}
                      {isSolo2 && idx === 0 && (
                        <div className="sf-week-divider sf-week-divider-1">
                          <div className="sf-week-divider-inner">
                            <span className="sf-week-divider-badge sf-week-badge-1">Week 1</span>
                            <span className="sf-week-divider-range">{fmt(week1Sun)} – {fmt(week1Sat)}</span>
                          </div>
                        </div>
                      )}

                      {/* ── Week 2 header (shown before block index 4) ── */}
                      {showWeek2Divider && (
                        <div className="sf-week-divider sf-week-divider-2">
                          <div className="sf-week-divider-inner">
                            <span className="sf-week-divider-badge sf-week-badge-2">Week 2</span>
                            <span className="sf-week-divider-range">{fmt(week2Sun)} – {fmt(week2Sat)}</span>
                          </div>
                        </div>
                      )}

                      <div className={`sf-block ${isSolo2 && idx >= SOLO2_WEEK1_COUNT ? "sf-block-week2" : ""}`}>
                        <div className="sf-block-label">
                          <span className="sf-block-num">{idx + 1}</span>
                          {blockLabels[idx]}
                        </div>
                        <div className="sf-block-fields">
                          <div className="sf-group">
                            <label>Start Date</label>
                            <input
                              type="date"
                              value={block.startDate}
                              onChange={(e) => handleBlockChange(idx, "startDate", e.target.value)}
                            />
                          </div>
                          <div className="sf-group">
                            <label>End Date <span className="sf-auto-tag">auto</span></label>
                            <input
                              type="date"
                              value={block.endDate}
                              onChange={(e) => handleBlockChange(idx, "endDate", e.target.value)}
                            />
                          </div>
                          <div className="sf-group">
                            <label>Start Time</label>
                            <TimePicker24
                              value={block.startTime}
                              onChange={(val) => handleBlockChange(idx, "startTime", val)}
                            />
                          </div>
                          <div className="sf-group">
                            <label>End Time</label>
                            <TimePicker24
                              value={block.endTime}
                              onChange={(val) => handleBlockChange(idx, "endTime", val)}
                            />
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>

              {/* ── Notes (inside Blocks section) ── */}
              <div className="sf-section-title">
                <span><i className="fas fa-sticky-note"></i> Notes</span>
              </div>
              <div className="sf-group">
                <textarea
                  className="sf-textarea"
                  placeholder="Any additional notes for this week..."
                  value={formData.notes}
                  onChange={(e) => setFormData((f) => ({ ...f, notes: e.target.value }))}
                  rows={4}
                />
              </div>

            </div>
          )}

          {/* ── Actions ── */}
          <div className="sf-actions">
            <button type="button" className="sf-btn-secondary" onClick={resetForm} disabled={saving}>
              <i className="fas fa-times"></i> Clear
            </button>
            <button type="submit" className="sf-btn-primary" disabled={saving}>
              {saving
                ? <><i className="fas fa-spinner fa-spin"></i> Saving...</>
                : <><i className="fas fa-calendar-check"></i> Save Schedule</>}
            </button>
          </div>

        </form>
      </div>
    </Layout>
  );
};

export default NewScheduleForm;